/**
 * The `SelfAppraisalContainer` component in the TypeScript React code manages self appraisal data,
 * including OKRs and nominations, with functionality for saving drafts, submitting, and handling
 * notifications.
 * @returns The `SelfAppraisalContainer` component is being returned. This component contains the main
 * form for self appraisal data including OKRs and nominations. It handles various functionalities such
 * as updating OKRs, handling nomination changes, adding/removing OKRs, submitting self appraisal,
 * saving drafts, refreshing data, and displaying notifications through a dialog component. The
 * component also includes loading states, error handling, and validation logic for
 */
import React, { useEffect, useCallback, useState, useRef, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import { store } from "../store";
import { useDialog } from "../hooks/useDialog";
import { useLocation } from 'react-router-dom';
import { useRBAC } from "../hooks/useRBAC";
import { CycleType } from "../types";
import SelfAppraisalPage from "../pages/features/self_appraisal/SelfAppraisalPage.tsx";
import Dialog from "../components/ui/Dialog";
import ErrorState from "../components/ui/ErrorState";
import NoActiveCycle from "../components/ui/NoActiveCycle";
import { selfAppraisalService } from "../services";
import type { Employee, NominationEntry } from "../services/selfAppraisalService";
import {
  updateOKR,
  addOKR,
  removeOKR,
  updateNomination,
  clearError,
  fetchSelfAppraisalData,
  saveDraftThunk,
  updateNominationsThunk,
  submitNominationThunk,
  submitAppraisalThunk,
} from "../store/selfAppraisalSlice";

const normalizeEmployees = (employees: Employee[]): Employee[] => employees.map((employee) => ({
  ...employee,
  id: employee.id ?? 0,
  isExternal: Boolean(employee.isExternal ?? employee.external ?? employee.id === 0),
}));

const SelfAppraisalContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { appraisalCycle } = useRBAC();
  const activeCycleType = appraisalCycle?.cycle_type;
  // Result and Rating are locked (and sent blank) only for Target Settings.
  const isEyrRatingLocked = activeCycleType === CycleType.START;
  const { dialogState, showDialog, showSuccess, showError, showWarning, closeDialog } =
    useDialog();

  // State for nomination data
  const [nominationData, setNominationData] = useState<{
    availableEmployees: {
      superiors: Employee[];
      dotted_superior: Employee[];
      subordinates: Employee[];
      additional_subordinates: Employee[];
      peers: Employee[];
      stakeholders: Employee[];
    };
    nominationManager: Employee[];
  }>({
    availableEmployees: {
      superiors: [],
      dotted_superior: [],
      subordinates: [],
      additional_subordinates: [],
      peers: [],
      stakeholders: [],
    },
    nominationManager: [],
  });

  // State for nomination approval status
  const [nominationStatus, setNominationStatus] = useState<string>("");
  
  // State for manager comments from nomination response
  const [managerComments, setManagerComments] = useState<string>("");
  
  // State to track if updated nominations are empty (for hasNominationData logic)
  const [isUpdatedNominationsEmpty, setIsUpdatedNominationsEmpty] = useState<boolean | null>(null);
  
  // State for nomination refresh loading
  const [isNominationRefreshing, setIsNominationRefreshing] = useState(false);
  
  // State for API submission errors
  const [apiErrorMessage, setApiErrorMessage] = useState<string>("");
  const [apiErrorTitle, setApiErrorTitle] = useState<string>("");

  const {
    okrs,
    nominations,
    isSubmitting,
    isSaving,
    error,
    apiData,
    isLoading,
  } = useAppSelector((state) => state.selfAppraisal);

  const { user } = useAppSelector((state) => state.auth);
  const userEmail = user?.email ?? null;

  const handleRetry = useCallback(() => {
    dispatch(clearError());
    if (userEmail) {
      dispatch(fetchSelfAppraisalData(userEmail));
    }
  }, [dispatch, userEmail]);

  const hasUnsavedChanges = useMemo(() => {
    if (!apiData || apiData.is_freezed) {
      return false;
    }

    const normalize = (list: typeof okrs) =>
      list.map((item) => ({
        objective: item.objective.trim(),
        target: item.target.trim(),
        endYearResult: item.endYearResult.trim(),
        rating: item.rating == null ? null : Number(item.rating),
      }));

    const serverOkrs: typeof okrs = Array.isArray(apiData.okrs)
      ? (selfAppraisalService.transformOKRsFromApiFormat(apiData.okrs) as typeof okrs)
      : ([] as typeof okrs);

    const normalizedLocal = normalize(okrs);
    const normalizedServer = normalize(serverOkrs);

    return JSON.stringify(normalizedLocal) !== JSON.stringify(normalizedServer);
  }, [apiData, okrs]);

  // Track unsaved nomination changes
  const [serverNominations, setServerNominations] = useState<{
    subordinates: Employee[];
    additional_subordinates: Employee[];
    peers: Employee[];
    stakeholders: Employee[];
    dotted_superior: Employee[];
  } | null>(null);

  const hasUnsavedNominationChanges = useMemo(() => {
    if (!serverNominations || nominationStatus !== 'draft') {
      return false;
    }

    const normalizeNominations = (list: Employee[]) =>
      list
        .map((emp) =>
          emp.isExternal || emp.id === 0
            ? `email:${emp.email}`
            : `employee:${emp.emp_id}`,
        )
        .sort();

    const localSubordinates = normalizeNominations(nominations.subordinates);
    const localAdditionalSubordinates = normalizeNominations(nominations.additional_subordinates);
    const localPeers = normalizeNominations(nominations.peers);
    const localStakeholders = normalizeNominations(nominations.stakeholders);
    const localDottedSuperiors = normalizeNominations(nominations.dotted_superior);

    const serverSubordinates = normalizeNominations(serverNominations.subordinates);
    const serverAdditionalSubordinates = normalizeNominations(serverNominations.additional_subordinates);
    const serverPeers = normalizeNominations(serverNominations.peers);
    const serverStakeholders = normalizeNominations(serverNominations.stakeholders);
    const serverDottedSuperiors = normalizeNominations(serverNominations.dotted_superior);

    return (
      JSON.stringify(localSubordinates) !== JSON.stringify(serverSubordinates) ||
      JSON.stringify(localAdditionalSubordinates) !== JSON.stringify(serverAdditionalSubordinates) ||
      JSON.stringify(localPeers) !== JSON.stringify(serverPeers) ||
      JSON.stringify(localStakeholders) !== JSON.stringify(serverStakeholders)
      || JSON.stringify(localDottedSuperiors) !== JSON.stringify(serverDottedSuperiors)
    );
  }, [nominations, serverNominations, nominationStatus]);

  // Helper function to extract error message from API response
  const getErrorMessage = useCallback((error: unknown): string => {
    if (error && typeof error === "object" && "response" in error) {
      const apiError = error as { response?: { message?: string } };
      if (apiError.response?.message) {
        return apiError.response.message;
      }
    }
    if (error && typeof error === "object" && "message" in error) {
      return (error as { message: string }).message;
    }
    if (typeof error === "string") {
      return error;
    }
    return "An unexpected error occurred. Please try again.";
  }, []);

  // Fetch nomination data
  const fetchNominationData = useCallback(
    async (email: string) => {
      try {
        const response = await selfAppraisalService.getNominationsDefault(
          email
        );
        console.log("Full getNominationsDefault response:", response);
        if (response.data) {
          const data = response.data;
          console.log("API Response data:", data);
          console.log("Superiors in API response:", data.superiors);
          
          setNominationData({
            availableEmployees: {
              superiors: normalizeEmployees(data.superiors || []),
              dotted_superior: normalizeEmployees(data.dotted_superior || data.dotted_superiors || []),
              subordinates: normalizeEmployees(data.subordinates || []),
              additional_subordinates: normalizeEmployees(
                data.additional_subordinate || data.additional_subordinates || [],
              ),
              peers: normalizeEmployees(data.peers || []),
              stakeholders: normalizeEmployees(data.stakeholders || []),
            },
            nominationManager: data.nomination_manager || [],
          });
          
          // Capture nomination deadline information
          setNominationDeadlineData({
            nomination_end_date: data.nomination_end_date,
            nomination_cycle_left: data.nomination_cycle_left,
          });
          
          // Only populate superiors from API data - user selects others manually
          if (data.superiors && data.superiors.length > 0) {
            console.log("Found superiors in API, populating:", data.superiors);
            dispatch(
              updateNomination({
                field: "superiors",
                value: normalizeEmployees(data.superiors),
              })
            );
            console.log("Populated superiors from API:", data.superiors.map((emp) => emp.full_name));
          } else {
            console.log("No superiors found in API response or empty array");
          }

          // Update Redux state with nomination manager
          if (data.nomination_manager && data.nomination_manager.length > 0) {
            dispatch(
              updateNomination({
                field: "nominationApprover",
                value:  data.nomination_manager[0].full_name.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
              })
            );
            // Also set the email for tooltip
            dispatch(
              updateNomination({
                field: "nominationApproverEmail",
                value: data.nomination_manager[0].email
              })
            );
          }
        }
      } catch (error) {
        console.error("Failed to fetch nomination data:", error);
        showError("Nomination Data Error", getErrorMessage(error));
      }
    },
    [dispatch, showError, getErrorMessage]
  );
  // Fetch nomination data
  const fetchUpdatedNominationData = useCallback(
    async (email: string) => {
      // show loading in UI while fetching updated nominations
      setIsNominationRefreshing(true);
      try {
        console.log("Fetching updated nominations for:", email);
        const response = await selfAppraisalService.getUpdatedNominations(email);
        // const apiMessage = response?.message || "Fetched updated nomination data";
        console.log("Updated nominations response:", response);

        if (response.data) {
          const data = response.data;
          
          
          // Set nomination status from the API response
          setNominationStatus(data.status || "");
          
          // Set manager comments from the API response
          setManagerComments(data.manager_comments || "");

          // Only update nomination fields if data exists
          if (data.nominations) {
            // Check if all user-selectable nomination arrays are empty (exclude superiors which are auto-populated)
            const isAllNominationsEmpty = (
              (!data.nominations.subordinate || data.nominations.subordinate.length === 0) &&
              (!(data.nominations.additional_subordinate || data.nominations.additional_subordinates)?.length) &&
              (!data.nominations.peer || data.nominations.peer.length === 0) &&
              (!data.nominations.stakeholder || data.nominations.stakeholder.length === 0) &&
              (!data.nominations.dotted_superior || data.nominations.dotted_superior.length === 0)
              // Note: Don't check superiors here as they are auto-populated and managed separately
            );
            
            // Update state to track if updated nominations are empty
            setIsUpdatedNominationsEmpty(isAllNominationsEmpty);
            console.log("Updated nominations empty status:", isAllNominationsEmpty);
            
            if (isAllNominationsEmpty) {
              // Clear nomination fields (except superiors which are auto-populated) when API returns empty arrays
              dispatch(updateNomination({ field: "subordinates", value: [] }));
              dispatch(updateNomination({ field: "additional_subordinates", value: [] }));
              dispatch(updateNomination({ field: "peers", value: [] }));
              dispatch(updateNomination({ field: "stakeholders", value: [] }));
              dispatch(updateNomination({ field: "dotted_superior", value: [] }));
              // Note: Don't clear superiors as they are auto-populated from initial API call
              
              console.log("All user-selectable nomination arrays are empty - superiors preserved");
              
              // Store server nominations for comparison
              setServerNominations({
                subordinates: [],
                additional_subordinates: [],
                peers: [],
                stakeholders: [],
                dotted_superior: [],
              });
            } else {
              // Process non-empty nominations - load as-is first
              const subordinateData = normalizeEmployees(data.nominations.subordinate || []);
              const additionalSubordinateData = normalizeEmployees(
                data.nominations.additional_subordinate || data.nominations.additional_subordinates || [],
              );
              const peerData = normalizeEmployees(data.nominations.peer || []);
              const stakeholderData = normalizeEmployees(data.nominations.stakeholder || []);
              const dottedSuperiorData = normalizeEmployees(data.nominations.dotted_superior || []);
              const superiorData = normalizeEmployees(data.nominations.superior || []);
              
              // CRITICAL: Deduplicate peers and stakeholders
              // Check for any overlaps and remove duplicates from BOTH lists
              const peerIds = new Set(peerData.map((p: Employee) => p.id));
              const peerEmails = new Set(peerData.map((p: Employee) => p.email).filter(Boolean));
              
              // Find duplicates by checking if any peer is also in stakeholders
              const duplicateIds = new Set<number>();
              const duplicateEmails = new Set<string>();
              
              stakeholderData.forEach((s: Employee) => {
                if (peerIds.has(s.id)) duplicateIds.add(s.id);
                if (s.email && peerEmails.has(s.email)) duplicateEmails.add(s.email);
              });
              
              // If there are duplicates, remove them from BOTH lists and log a warning
              let finalPeers = peerData;
              let finalStakeholders = stakeholderData;
              
              if (duplicateIds.size > 0 || duplicateEmails.size > 0) {
                console.warn("🚨 Found duplicates in server data between peers and stakeholders!");
                console.warn("Removing duplicates from BOTH lists. You'll need to re-add them to the correct list.");
                
                // Remove duplicates from peers
                finalPeers = peerData.filter((p: Employee) => {
                  const isDuplicate = duplicateIds.has(p.id) || 
                                     (p.email && duplicateEmails.has(p.email));
                  if (isDuplicate) {
                    console.warn(`  - Removed from peers: ${p.full_name}`);
                  }
                  return !isDuplicate;
                });
                
                // Remove duplicates from stakeholders
                finalStakeholders = stakeholderData.filter((s: Employee) => {
                  const isDuplicate = duplicateIds.has(s.id) || 
                                     (s.email && duplicateEmails.has(s.email));
                  if (isDuplicate) {
                    console.warn(`  - Removed from stakeholders: ${s.full_name}`);
                  }
                  return !isDuplicate;
                });
              }
              
              if (subordinateData.length > 0) {
                dispatch(
                  updateNomination({
                    field: "subordinates",
                    value: subordinateData,
                  })
                );
              }

              dispatch(updateNomination({
                field: "additional_subordinates",
                value: additionalSubordinateData,
              }));
              
              if (finalPeers.length > 0) {
                dispatch(
                  updateNomination({
                    field: "peers",
                    value: finalPeers,
                  })
                );
              }
              
              if (finalStakeholders.length > 0) {
                dispatch(
                  updateNomination({
                    field: "stakeholders",
                    value: finalStakeholders,
                  })
                );
              }
              
              if (superiorData.length > 0) {
                console.log("Found superiors in updated nominations:", superiorData);
                dispatch(
                  updateNomination({
                    field: "superiors",
                    value: superiorData,
                  })
                );
              }

              if (dottedSuperiorData.length > 0) {
                dispatch(
                  updateNomination({
                    field: "dotted_superior",
                    value: dottedSuperiorData,
                  })
                );
              }
              
              // Store server nominations for comparison (use deduplicated data)
              setServerNominations({
                subordinates: subordinateData,
                additional_subordinates: additionalSubordinateData,
                peers: finalPeers,
                stakeholders: finalStakeholders,
                dotted_superior: dottedSuperiorData,
              });
            }
          } else {
            // No nominations data found, consider it as empty
            setIsUpdatedNominationsEmpty(true);
          }

          // // Show API message in a dialog on success
          // showSuccess("Nominations", apiMessage);
        } else {
          // If no data, set status to draft and show API message (if any)
          setNominationStatus("draft");
          // showWarning(
          //   "Nominations",
          //   response?.message || "No nomination data available"
          // );
        }
      } catch (error) {
        console.error("Failed to fetch updated nomination data:", error);
        // On error, don't disable fields (assume not approved)
        setNominationStatus("draft");
        // Show API / error message in a dialog
        showError("Nominations Error", getErrorMessage(error));
      } finally {
        // Ensure loading state is cleared regardless of success/failure
        setIsNominationRefreshing(false);
      }
    },
  [dispatch, showError, getErrorMessage]
  );

  // Store stable refs to the fetch functions so effects can call them
  const fetchNominationDataRef = useRef<typeof fetchNominationData | null>(
    null
  );
  const fetchUpdatedNominationDataRef = useRef<
    typeof fetchUpdatedNominationData | null
  >(null);

  // Assign current functions to refs on each render (doesn't trigger effects)
  fetchNominationDataRef.current = fetchNominationData;
  fetchUpdatedNominationDataRef.current = fetchUpdatedNominationData;

  // Compute days-left from end date locally (midnight-based) to avoid timezone off-by-one
  const computeDaysLeftFromEndDate = (endDateStr?: string | null): number | undefined => {
    if (!endDateStr) return undefined;
    const end = new Date(endDateStr);
    if (Number.isNaN(end.getTime())) return undefined;
    const today = new Date();
    const endLocal = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.floor((endLocal.getTime() - todayLocal.getTime()) / msPerDay);
  };

  // Separate deadline computations for objectives and nominations
  const computedObjectivesDaysLeft = apiData?.self_appraisal_end_date
    ? computeDaysLeftFromEndDate(apiData.self_appraisal_end_date)
    : apiData?.self_appraisal_cycle_left;

  // For nominations, we need to get this from the nomination API response
  // For now, use a separate state until we get nomination deadline from API
  const [nominationDeadlineData, setNominationDeadlineData] = useState<{
    nomination_end_date?: string;
    nomination_cycle_left?: number;
  }>({});

  const computedNominationDaysLeft = nominationDeadlineData?.nomination_end_date
    ? computeDaysLeftFromEndDate(nominationDeadlineData.nomination_end_date)
    : nominationDeadlineData?.nomination_cycle_left;

  // Check if nomination data is not blank - based on your specific logic
  // If isAllNominationsEmpty is false, then hasNominationData is false, else true
  // Also validate that required fields (peers and subordinates if available) are filled
  const hasNominationData = (() => {
    // First check if we have any nominations at all
    const hasAnyNominations = isUpdatedNominationsEmpty !== null 
      ? isUpdatedNominationsEmpty // If isAllNominationsEmpty is true, then hasNominationData is true
      : Boolean( // Fallback to checking current nominations if fetchUpdatedNominationData hasn't run yet
          nominations.superiors.length > 0 ||
          nominations.subordinates.length > 0 ||
          nominations.additional_subordinates.length > 0 ||
          nominations.peers.length > 0 ||
          nominations.stakeholders.length > 0
        );
    
    // If no nominations at all, return true (submit button should be disabled)
    if (hasAnyNominations) {
      return true;
    }
    
    // Check if required fields are missing
    // If peers are available, they must be selected
    const peersAvailable = nominationData?.availableEmployees?.peers && nominationData.availableEmployees.peers.length > 0;
    const peersSelected = nominations.peers.length > 0;
    
    const subordinatesRequired =
      (nominationData?.availableEmployees?.subordinates.length ?? 0) > 0;
    const subordinatesSelected = nominations.subordinates.length > 0;
    
    // Return true (disable submit) if required fields are not filled
    if (peersAvailable && !peersSelected) {
      return true;
    }
    
    if (subordinatesRequired && !subordinatesSelected) {
      return true;
    }

    // All required fields are filled, enable submit
    return false;
  })();
      
  console.log("hasNominationData calculation:", {
    isUpdatedNominationsEmpty,
    hasNominationData,
    nominations: {
      superiors: nominations.superiors.length,
      subordinates: nominations.subordinates.length,
      additional_subordinates: nominations.additional_subordinates.length,
      peers: nominations.peers.length,
      stakeholders: nominations.stakeholders.length
    }
  });

  // Also store showError and getErrorMessage in refs to avoid re-running
  // the initial fetch effect when these callbacks change identity.
  const showErrorRef = useRef(showError);
  const getErrorMessageRef = useRef(getErrorMessage);
  showErrorRef.current = showError;
  getErrorMessageRef.current = getErrorMessage;

  // Rate-limit repetitive self-appraisal fetches
  const lastFetchRef = useRef<number>(0);
  const performFetchSelfAppraisal = useCallback(
    async (email: string, options?: { force?: boolean }) => {
      const now = Date.now();
      const MIN_INTERVAL = 3000; // 3 seconds
      // If not forced, enforce minimum interval between calls
      if (!options?.force && now - lastFetchRef.current < MIN_INTERVAL) {
        console.log("Skipping rapid fetchSelfAppraisalData call");
        console.trace();
        return;
      }
      lastFetchRef.current = now;

      try {
        console.log("Dispatching fetchSelfAppraisalData for", email, "force=", !!options?.force);
        await dispatch(fetchSelfAppraisalData(email)).unwrap();
        console.log("Successfully loaded self appraisal data (guarded)");
      } catch (error) {
        console.error("Guarded fetchSelfAppraisalData failed:", error);
        // Use ref showError to avoid dependency issues
        try {
          if (showErrorRef.current) {
            showErrorRef.current(
              "Failed to Load Data",
              getErrorMessageRef.current(error)
            );
          }
        } catch (err) {
          console.error("Failed to call showError in guarded fetch:", err);
        }
      }
    },
    [dispatch]
  );

  // Guard to ensure initial fetch runs only once per mount
  const hasFetchedRef = useRef(false);

  // Helper function to check if we're on nominations tab
  const isOnNominationsTab = useCallback(() => {
    // Check URL path first
    const isNominationsPath = location.pathname.includes('/nominations') || location.pathname.includes('/nomination');
    
    // Check localStorage as backup
    let isNominationsFromStorage = false;
    try {
      const storedTab = localStorage.getItem("selfAppraisal_activeTab");
      isNominationsFromStorage = storedTab === "nominations";
    } catch {
      // ignore localStorage errors
    }
    
    return isNominationsPath || isNominationsFromStorage;
  }, [location.pathname]);

  // Fetch self appraisal data on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.email) {
        console.error("User email not available");
        return;
      }

      if (hasFetchedRef.current) {
        // already fetched once, skip
        return;
      }

      try {
        // Always fetch OKR data first
        await performFetchSelfAppraisal(user.email);
        console.log("Initial OKR data fetch completed");
        
        // If user is on nominations tab during page refresh, also fetch nomination APIs
        if (isOnNominationsTab()) {
          console.log("User is on nominations tab during refresh - fetching nomination APIs");
          
          try {
            // Fetch default nomination lists (superiors, available employees)
            if (fetchNominationDataRef.current) {
              await fetchNominationDataRef.current(user.email);
              console.log("Default nomination data fetch completed on refresh");
            }
            
            // Fetch any previously saved/updated nominations and status
            if (fetchUpdatedNominationDataRef.current) {
              await fetchUpdatedNominationDataRef.current(user.email);
              console.log("Updated nomination data fetch completed on refresh");
            }
          } catch (nominationError) {
            console.error("Failed to fetch nomination data on refresh:", nominationError);
            // Don't throw error here as OKR data was successfully fetched
          }
        }
        
        hasFetchedRef.current = true;
        console.log("Initial guarded fetch completed");
      } catch (error) {
        console.error("Failed to fetch self appraisal data:", error);
        try {
          if (showErrorRef.current) {
            showErrorRef.current(
              "Failed to Load Data",
              getErrorMessageRef.current(error)
            );
          }
        } catch (err) {
          console.error("Failed to call showError:", err);
        }
      }
    };

    fetchData();
  }, [user?.email, dispatch, performFetchSelfAppraisal, isOnNominationsTab]);

  const handleOKRChange = (
    id: string,
    field: keyof (typeof okrs)[0],
    value: string | number | null
  ) => {
    dispatch(updateOKR({ id, field, value }));
  };

  const handleNominationChange = (
    field: keyof typeof nominations,
    value: string | Employee[]
  ) => {
    console.log(`🔄 handleNominationChange called:`, {
      field,
      valueType: Array.isArray(value) ? 'array' : 'string',
      valueCount: Array.isArray(value) ? value.length : 'N/A',
      valueNames: Array.isArray(value) ? value.map(v => v.full_name) : value,
    });
    
    // If adding employees to peers or stakeholders, remove them from the other list
    if (Array.isArray(value) && (field === 'peers' || field === 'stakeholders')) {
      const otherField = field === 'peers' ? 'stakeholders' : 'peers';
      
      // CRITICAL FIX: Get the LATEST state from Redux store, not from closure
      // This prevents stale state issues when multiple updates happen in quick succession
      const currentNominations = store.getState().selfAppraisal.nominations;
      const otherList = currentNominations[otherField] as Employee[];
      
      console.log(`📋 Current ${otherField} before deduplication (from store):`, {
        count: otherList.length,
        names: otherList.map(e => e.full_name)
      });
      
      // Get IDs of employees being added to current field
      const currentIds = new Set(value.map(emp => emp.id));
      const currentEmails = new Set(value.map(emp => emp.email).filter(Boolean));
      
      // Filter out any employees from the other list that are now in current list
      const filteredOtherList = otherList.filter(emp => {
        const isDuplicate = currentIds.has(emp.id) || 
                           (emp.email && currentEmails.has(emp.email));
        return !isDuplicate;
      });
      
      // If we filtered out any employees from the other list, update it FIRST
      // This ensures both updates are processed together
      if (filteredOtherList.length !== otherList.length) {
        console.log(`🔄 Removing duplicates from ${otherField}:`, {
          before: otherList.length,
          after: filteredOtherList.length,
          removedCount: otherList.length - filteredOtherList.length,
          remainingNames: filteredOtherList.map(e => e.full_name)
        });
        dispatch(updateNomination({ field: otherField, value: filteredOtherList }));
      }
    }
    
    // Update the current field
    console.log(`✅ Updating ${field}:`, {
      count: Array.isArray(value) ? value.length : 'N/A',
      names: Array.isArray(value) ? value.map(v => v.full_name) : value
    });
    dispatch(updateNomination({ field, value }));
    dispatch(clearError());
  };

  const handleAddOKR = () => {
    dispatch(addOKR());
  };

  const handleRemoveOKR = (id: string) => {
    dispatch(removeOKR(id));
  };

  // Refresh functions for tab switching - memoized to prevent unnecessary re-renders
  const handleRefreshObjectives = useCallback(async () => {
    if (!user?.email) {
      console.error("User email not available for refreshing objectives");
      return;
    }

    try {
      await performFetchSelfAppraisal(user.email);
      console.log("Objectives data refreshed successfully (guarded)");
    } catch (error) {
      console.error("Failed to refresh objectives data:", error);
      showError(
        "Refresh Error",
        `Failed to refresh objectives data: ${getErrorMessage(error)}`
      );
    }
  }, [user?.email, performFetchSelfAppraisal, showError, getErrorMessage]);

  const handleRefreshNominations = useCallback(async () => {
    if (!user?.email) {
      console.error("User email not available for refreshing nominations");
      return;
    }

    try {
      // Fetch default nomination lists (superiors, available employees)
      if (fetchNominationDataRef.current)
        await fetchNominationDataRef.current(user.email);
      // Fetch any previously saved/updated nominations and status
      if (fetchUpdatedNominationDataRef.current)
        await fetchUpdatedNominationDataRef.current(user.email);
      console.log("Nominations data refreshed successfully");
    } catch (error) {
      console.error("Failed to refresh nominations data:", error);
      showError(
        "Refresh Error",
        `Failed to refresh nominations data: ${getErrorMessage(error)}`
      );
    }
  }, [user?.email, showError, getErrorMessage]);

  // Save draft functionality
  const handleSaveDraft = async () => {
    try {
      const trimmedOkrs = okrs.map((okr) => ({
        ...okr,
        objective: okr.objective.trim(),
        target: okr.target.trim(),
        endYearResult: isEyrRatingLocked ? "" : okr.endYearResult.trim(),
        rating: isEyrRatingLocked ? null : okr.rating,
      }));

      const isBlankOkr = (okr: (typeof okrs)[number]) =>
        !okr.objective &&
        !okr.target &&
        (isEyrRatingLocked || (!okr.endYearResult && (okr.rating == null || Number.isNaN(okr.rating))));

      const blankOkrs = trimmedOkrs.filter(isBlankOkr);
      const okrsWithData = trimmedOkrs.filter((okr) => !isBlankOkr(okr));

      const blanksToRemove = okrsWithData.length > 0 ? blankOkrs : blankOkrs.slice(1);
      blanksToRemove.forEach((blank) => {
        dispatch(removeOKR(blank.id));
      });

      if (okrsWithData.length === 0) {
        if (trimmedOkrs.length > 0) {
          showWarning(
            "No OKRs to Save",
            "Add objective details before saving your draft."
          );
        }
        return;
      }

      // For draft saving, skip validation - save whatever data exists
      // const validation = selfAppraisalService.validateOKRs(okrsWithData);
      // if (!validation.isValid) {
      //   return;
      // }

      const resultAction = await dispatch(
        saveDraftThunk({
          email: apiData?.email || "",
          id: apiData?.id || 0,
          cycle_type: activeCycleType,
          okrs: okrsWithData.map((okr) => ({
            id: okr.id,
            objective: okr.objective,
            target: okr.target,
            endYearResult: isEyrRatingLocked ? "" : okr.endYearResult,
            rating: isEyrRatingLocked ? null : (okr.rating ?? null),
          })),
        })
      );
      if (saveDraftThunk.fulfilled.match(resultAction)) {
        showSuccess("Success", resultAction.payload.message);
      } else if (saveDraftThunk.rejected.match(resultAction)) {
        const errorMessage = getErrorMessage(resultAction.error.message);
        showError("Error", `Failed to save draft: ${errorMessage}`);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError("Error", `Error saving draft: ${errorMessage}`);
    }
  };

  // Save nominations functionality
  const handleSaveDraftNomination = useCallback(async () => {
    try {
      if (!apiData?.email) {
        showError("Error", "Email not available");
        return;
      }

      // Get employee IDs from nomination data
      // Use 'id' field which contains:
      // - For saved nominations: nomination relationship ID (e.g., 344)
      // - For new selections: employee ID (e.g., 2363)
      // Note: emp_id can be a string like "G032" so we can't use it
      const getEmployeeIds = (
        employees: Employee[]
      ): NominationEntry[] => {
        
        return employees
          .map((emp) => {
            console.log("Processing employee for ID extraction:", JSON.stringify(emp));
            // Always use 'id' field
            // Backend is smart enough to handle both nomination IDs and employee IDs
            return {
              id: emp.isExternal || emp.external || emp.id === 0 ? null : emp.id,
              email: emp.email,
              external: Boolean(emp.isExternal || emp.external || emp.id === 0),
            };
          })
          .filter((entry) => Boolean(entry.email));
      };

      const subordinateIds = getEmployeeIds(
        nominations.subordinates
      );
      const additionalSubordinateIds = getEmployeeIds(
        nominations.additional_subordinates
      );
      const peerIds = getEmployeeIds(
        nominations.peers
      );
      const stakeholderIds = getEmployeeIds(
        nominations.stakeholders
      );
      const dottedSuperiorIds = getEmployeeIds(
        nominations.dotted_superior
      );
      const superiorIds = getEmployeeIds(
        nominations.superiors
      );

      // Log the current state before saving
      console.log("💾 Saving nominations with current state:", {
        peers: {
          count: nominations.peers.length,
          ids: peerIds,
          names: nominations.peers.map(p => p.full_name)
        },
        stakeholders: {
          count: nominations.stakeholders.length,
          ids: stakeholderIds,
          names: nominations.stakeholders.map(s => s.full_name)
        },
        subordinates: {
          count: nominations.subordinates.length,
          ids: subordinateIds,
        },
        superiors: {
          count: nominations.superiors.length,
          ids: superiorIds,
        }
      });

      const resultAction = await dispatch(
        updateNominationsThunk({
          email: apiData.email,
          subordinate: subordinateIds,
          additional_subordinate: additionalSubordinateIds,
          peer: peerIds,
          stakeholder: stakeholderIds,
          superior: superiorIds,
          dotted_superior: dottedSuperiorIds,
        })
      );

      if (updateNominationsThunk.fulfilled.match(resultAction)) {
        showSuccess("Success", "Nominations saved successfully");
        
        // Update server nominations to match current state after save
        // This marks the current state as "saved" so we can detect future unsaved changes
        setServerNominations({
          subordinates: [...nominations.subordinates],
          additional_subordinates: [...nominations.additional_subordinates],
          peers: [...nominations.peers],
          stakeholders: [...nominations.stakeholders],
          dotted_superior: [...nominations.dotted_superior],
        });
        
        // BEST PRACTICE: Don't refresh immediately after save to prevent data loss
        // The API response takes time to reflect the changes, and refreshing too soon
        // can cause a race condition where old data overwrites the new data
        // Instead, we trust the save succeeded and keep the UI state as-is
        // Data will be refreshed when:
        // 1. User switches tabs (handleRefreshNominations is called on tab change)
        // 2. User manually refreshes
        // 3. User reloads the page
        console.log("✅ Nominations saved - keeping current UI state (no immediate refresh)");
        
      } else if (updateNominationsThunk.rejected.match(resultAction)) {
        const errorMessage = getErrorMessage(resultAction.error.message);
        showError("Error", `Error saving nominations: ${errorMessage}`);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError("Error", `Error saving nominations: ${errorMessage}`);
    }
  }, [apiData?.email, nominations, dispatch, showSuccess, showError, getErrorMessage]);

  // Submit nominations functionality
  const handleSubmitNomination = async (skipUnsavedCheck: boolean = false) => {
    try {
      if (!apiData?.email) {
        showError("Error", "Email not available");
        return;
      }

      // Check if there are unsaved nomination changes (skip if we just saved)
      if (!skipUnsavedCheck && hasUnsavedNominationChanges) {
        console.log("⚠️ Unsaved nomination changes detected before submit");
        showDialog({
          title: "Unsaved Changes",
          message: "You have unsaved nomination changes. Would you like to save as draft and then submit?",
          type: "warning",
          showCancel: true,
          confirmText: "Save Draft & Continue",
          cancelText: "Cancel",
          onConfirm: async () => {
            closeDialog();
            // Save the draft first
            await handleSaveDraftNomination();
            // After save completes, proceed to submit with skipUnsavedCheck=true
            // This prevents infinite loop by skipping the unsaved check
            setTimeout(() => {
              handleSubmitNomination(true); // Skip unsaved check on retry
            }, 500);
          },
        });
        return;
      }

      // Validate nominations before submission
      const missingFields: string[] = [];
      const validationErrors: string[] = [];
      
      // Check if peers are available and required (minimum 1, maximum 10)
      if (nominationData?.availableEmployees?.peers && nominationData.availableEmployees.peers.length > 0) {
        if (nominations.peers.length === 0) {
          missingFields.push("Peers (minimum 1 required)");
        } else if (nominations.peers.length > 10) {
          validationErrors.push("Peers: Maximum 10 peers allowed");
        }
      }
      
      // Require a subordinate whenever the available list is populated.
      if (nominationData?.availableEmployees.subordinates.length) {
        if (nominations.subordinates.length === 0) {
          missingFields.push("Subordinates");
        }
      }
      
      // If there are missing required fields, show error and return
      if (missingFields.length > 0) {
        showError(
          "Validation Error",
          `Please select at least one nomination for: ${missingFields.join(", ")}`
        );
        return;
      }
      
      // If there are validation errors (like exceeding max), show error and return
      if (validationErrors.length > 0) {
        showError(
          "Validation Error",
          validationErrors.join("\n")
        );
        return;
      }

      // Get nomination IDs from nomination data
      // Use 'id' field which contains:
      // - For saved nominations: nomination relationship ID (e.g., 344)
      // - For new selections: employee ID (e.g., 2363)
      // Note: emp_id can be a string like "G032" so we can't use it
      const getEmployeeIds = (
        employees: Employee[]
      ): NominationEntry[] => {
        return employees
          .map((emp) => {
            // Always use 'id' field
            // Backend is smart enough to handle both nomination IDs and employee IDs
            return {
              id: emp.isExternal || emp.external || emp.id === 0 ? null : emp.id,
              email: emp.email,
              external: Boolean(emp.isExternal || emp.external || emp.id === 0),
            };
          })
          .filter((entry) => Boolean(entry.email));
      };

      const subordinateIds = getEmployeeIds(
        nominations.subordinates
      );
      const additionalSubordinateIds = getEmployeeIds(
        nominations.additional_subordinates
      );
      const peerIds = getEmployeeIds(
        nominations.peers
      );
      const stakeholderIds = getEmployeeIds(
        nominations.stakeholders
      );
      const dottedSuperiorIds = getEmployeeIds(
        nominations.dotted_superior
      );
      const superiorIds = getEmployeeIds(
        nominations.superiors
      );

      const resultAction = await dispatch(
        submitNominationThunk({
          email: apiData.email,
          subordinate: subordinateIds,
          additional_subordinate: additionalSubordinateIds,
          peer: peerIds,
          stakeholder: stakeholderIds,
          superior: superiorIds,
          dotted_superior: dottedSuperiorIds,
        })
      );

      if (submitNominationThunk.fulfilled.match(resultAction)) {
        showSuccess("Success", "Nominations submitted successfully");
        try {
          // Refresh nomination lists and status after successful submission
          await handleRefreshNominations();
          console.log("Refreshed nominations after submit");
        } catch (refreshErr) {
          console.error("Failed to refresh nominations after submit:", refreshErr);
        }
      } else if (submitNominationThunk.rejected.match(resultAction)) {
        const errorMessage = getErrorMessage(resultAction.error.message);
        showError("Error", `Error submitting nominations: ${errorMessage}`);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError("Error", `Error submitting nominations: ${errorMessage}`);
    }
  };

  // Final submission functionality
  const handleSubmit = async () => {
    console.log("🔍 handleSubmit called - Checking data availability:");
    console.log("User email:", user?.email);
    console.log("ApiData:", apiData);
    console.log("ApiData ID:", apiData?.id);
    
    // Clear any previous API errors
    setApiErrorMessage("");
    setApiErrorTitle("");
    
    if (!user?.email || !apiData?.id) {
      const missingData = [];
      if (!user?.email) missingData.push("user email");
      if (!apiData?.id) missingData.push("appraisal ID");
      
      console.error("❌ User information or appraisal ID not available. Missing:", missingData.join(", "));
      showError(
        "Unable to Submit", 
        `Cannot submit because ${missingData.join(" and ")} ${missingData.length > 1 ? 'are' : 'is'} not available. Please refresh the page and try again.`
      );
      return;
    }

    // Validate OKRs before submission
    const validation = selfAppraisalService.validateOKRs(okrs, activeCycleType);
    if (!validation.isValid) {
      showError(
        "Validation Error",
        `Please fix the following errors:\n${validation.errors.join("\n")}`
      );
      return;
    }

    // Check if already frozen
    if (apiData.is_freezed) {
      showWarning(
        "Already Submitted",
        "This self appraisal has already been submitted and cannot be modified."
      );
      return;
    }

    try {
      await dispatch(
        submitAppraisalThunk({
          email: user.email,
          id: apiData.id,
          cycle_type: activeCycleType,
          okrs: okrs.map((okr) => ({
            ...okr,
            endYearResult: isEyrRatingLocked ? "" : okr.endYearResult,
            rating: isEyrRatingLocked ? null : okr.rating,
          })),
        })
      ).unwrap();

      showSuccess(
        "Success",
        "Self appraisal submitted successfully! You can no longer make changes."
      );
      // Refresh objectives so the UI receives the latest (frozen) data
      try {
        // Force refresh to bypass rate-limit when coming from an explicit user submit
        await performFetchSelfAppraisal(user.email, { force: true });
        console.log("Refreshed objectives after submit (forced)");
      } catch (refreshError) {
        console.error("Failed to refresh objectives after submit:", refreshError);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      
      // Clear any previous API errors first
      setApiErrorMessage("");
      setApiErrorTitle("");
      
      // Check if this is a draft-related error that should be shown in the page modal
      const errorStr = errorMessage.toLowerCase();
      const isDraftError = errorStr.includes('draft') || errorStr.includes('save') || errorStr.includes('match');
      
      if (isDraftError) {
        // Set API error state to be displayed by the page component
        setApiErrorTitle("Save Draft Required");
        setApiErrorMessage(errorMessage);
      } else {
        // For other errors, continue to use the dialog system
        showError(
          "Submission Failed",
          `Failed to submit appraisal: ${errorMessage}`
        );
      }
    }
  };

  // Validation helper
  const getValidationState = () => {
    const validation = selfAppraisalService.validateOKRs(okrs, activeCycleType);
    return {
      isFormValid: validation.isValid,
      validationErrors: validation.errors,
    };
  };

  const normalizedError = typeof error === 'string' ? error.toLowerCase() : '';
  const isNoCycleError = Boolean(normalizedError) && (
    normalizedError.includes('no active') ||
    normalizedError.includes('no appraisal') ||
    (normalizedError.includes('cycle') && (normalizedError.includes('no ') || normalizedError.includes('not '))) ||
    normalizedError.includes('not found')
  );
  const isNoDataFoundError = Boolean(normalizedError) && (
    normalizedError.includes('no okr') ||
    normalizedError.includes('no nomination') ||
    normalizedError.includes('no self appraisal data') ||
    normalizedError.includes('no appraisal data') ||
    normalizedError.includes('no data found for okr') ||
    normalizedError.includes('no data found for nomination') ||
    normalizedError.includes('no data found')
  );

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading self appraisal data...</p>
        </div>
      </div>
    );
  }

  // Show error if there's an error and no data
  if (error && !isLoading) {
    const errorMessage = error;
    const derivedErrorType: 'server-error' | 'bad-gateway' | 'timeout' | 'network-error' = normalizedError.includes('timeout') || normalizedError.includes('504')
      ? 'timeout'
      : normalizedError.includes('gateway') || normalizedError.includes('502')
        ? 'bad-gateway'
        : normalizedError.includes('network') || normalizedError.includes('connection')
          ? 'network-error'
          : 'server-error';
    const isBadGatewayError = derivedErrorType === 'bad-gateway';

    if (isNoCycleError) {
      return <NoActiveCycle />;
    }

    if (isBadGatewayError) {
      return (
        <ErrorState
          type="bad-gateway"
          title="Bad Gateway"
          message={errorMessage || 'The server encountered a temporary error. Please try again shortly.'}
          onRetry={handleRetry}
          showRetryButton={true}
        />
      );
    }

    if (isNoDataFoundError) {
    
 return (
        <div className="flex min-h-full flex-col items-center justify-center px-4 py-4 sm:px-8 lg:px-24">
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
            <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Self Appraisal Data Found</h3>
          <p className="text-gray-500 mb-4">
            We couldn't find any self appraisal data for your account. This may be because there is no active appraisal cycle, or your account is not eligible for self appraisal at this time.
          </p>
        </div></div>
      );
    }

    const errorTitle =
      derivedErrorType === 'timeout'
        ? 'Request Timeout'
        : derivedErrorType === 'network-error'
          ? 'Connection Problem'
          : 'Oops! Something went wrong';

    return (
      <ErrorState
        type={derivedErrorType}
        title={errorTitle}
        message={errorMessage}
        onRetry={handleRetry}
        showRetryButton={true}
      />
    );
  }
  return (
    
    <div>
     
   
      {/* Main Form */}
      <SelfAppraisalPage
        okrs={okrs}
        nominations={nominations}
        availableNominations={{
          superiors: nominationData.availableEmployees.superiors,
          dotted_superior: nominationData.availableEmployees.dotted_superior,
          subordinates: nominationData.availableEmployees.subordinates,
          additional_subordinates: nominationData.availableEmployees.additional_subordinates,
          peers: nominationData.availableEmployees.peers,
          stakeholders: nominationData.availableEmployees.stakeholders,
        }}
        onOKRChange={handleOKRChange}
        onNominationChange={handleNominationChange}
        onAddOKR={handleAddOKR}
        onRemoveOKR={handleRemoveOKR}
        onSubmit={handleSubmit}
        onSubmitNomination={handleSubmitNomination}
        onSaveDraftOKR={handleSaveDraft}
        onSaveDraftNomination={handleSaveDraftNomination}
        onRefreshObjectives={handleRefreshObjectives}
        onRefreshNominations={handleRefreshNominations}
        isSubmitting={isSubmitting}
        isSaving={isSaving}
        isEditable={apiData ? !apiData.is_freezed : true}
        isNominationEditable={nominationStatus !== "approved"}
        cycleType={activeCycleType}
        immediateManager={apiData?.immediate_manager}
        immediateManagerEmail={apiData?.immediate_manager_email}
        daysLeft={computedObjectivesDaysLeft}
        nominationDaysLeft={computedNominationDaysLeft}
        isFormValid={getValidationState().isFormValid}
        validationErrors={getValidationState().validationErrors}
        canSaveDraft={Boolean(user?.email && apiData?.is_freezed === false)}
        draftExists={Boolean(Array.isArray(apiData?.okrs) && apiData?.okrs.length > 0)}
        isNominationSubmitEnabled={hasNominationData}
        isNominationDisabled={nominationStatus === "approved"}
        isNominationRefreshing={isNominationRefreshing}
        hasUnsavedNominationChanges={hasUnsavedNominationChanges}
        nominationStatus={nominationStatus}
        managerComments={managerComments}
        okrComments={apiData?.comment || ""}
        okrStatus={apiData?.status || ""}
        apiErrorMessage={apiErrorMessage}
        apiErrorTitle={apiErrorTitle}
        isFreezed={apiData ? apiData.is_freezed : false}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      {/* Dialog for notifications */}
      <Dialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        onConfirm={dialogState.onConfirm || closeDialog}
        showCancel={dialogState.showCancel}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
      />
    </div>
  );
};

export default SelfAppraisalContainer;
